import type { DTADocumentContent, MetadataVariable } from "@/generated/sdk"
/**
 * @file Shared, editor-independent date-format readiness check over a DTA's document_content.
 *   Mirrors the backend rule in
 *   packages/backend/lambda/dta-workflow-management/publish_validation.py (step 6) so the client
 *   and server agree on when a Date/Datetime column is publish-ready, and so the check runs from
 *   any tab (not only when the mounted editor exposes it, see #1241).
 */
import { DateFormat, MetadataVariableFormatEnum } from "@/generated/sdk"

interface PublishValidationError {
  field: string
  tab: string
  message: string
}

const DATE_FORMAT_TYPES = new Set<string>([
  MetadataVariableFormatEnum.Date,
  MetadataVariableFormatEnum.Datetime,
])

// The 8 supported date tokens, sourced from the generated SDK (published by the backend).
const SUPPORTED_DATE_FORMAT_TOKENS = Object.values(DateFormat)
const CANONICAL_BY_LOWER = new Map(
  SUPPORTED_DATE_FORMAT_TOKENS.map((token) => [token.toLowerCase(), token] as const),
)

const trimmed = (raw: string | null | undefined): string => (raw ?? "").trim()

/**
 * Whether a variable's declared Format is a Date or Datetime type.
 */
export const isDateFormatType = (format: string | null | undefined): boolean =>
  DATE_FORMAT_TYPES.has(format ?? "")

/**
 * Canonical supported token for a raw data_format (case-insensitive), or undefined when the value
 * is blank or unsupported. Callers that only need a yes/no use isSupportedDateFormat.
 */
export const canonicalDateFormat = (raw: string | null | undefined): string | undefined =>
  CANONICAL_BY_LOWER.get(trimmed(raw).toLowerCase())

/**
 * Whether data_format is a supported date token (case-insensitive). Blank is not supported.
 */
export const isSupportedDateFormat = (raw: string | null | undefined): boolean =>
  canonicalDateFormat(raw) !== undefined

/**
 * A Date/Datetime variable whose data_format is left blank ("Unspecified").
 */
export const isDateFormatMissing = (variable: MetadataVariable): boolean =>
  isDateFormatType(variable.format) && trimmed(variable.data_format) === ""

const variableLabel = (variable: MetadataVariable): string =>
  trimmed(variable.variable_name) || "(unnamed)"

/**
 * Date-format readiness errors for a DTA's document_content. A Date/Datetime column must declare a
 * non-blank, supported data_format; blank ("Unspecified") and unsupported tokens are both invalid.
 * Non-date variables are ignored. Pure and editor-independent.
 */
export function validateDateFormats(
  content: DTADocumentContent | null | undefined,
): PublishValidationError[] {
  const variables = content?.transfer_metadata_schema?.variables ?? []
  const dateVars = variables.filter((v) => isDateFormatType(v.format))
  const errors: PublishValidationError[] = []

  const missing = dateVars.filter((v) => trimmed(v.data_format) === "").map(variableLabel)
  if (missing.length > 0) {
    errors.push({
      field: "transfer_metadata_schema.variables",
      tab: "transfer-metadata",
      message:
        `Specify a date format on: ${missing.join(", ")}. ` +
        "Date/Datetime fields cannot be left Unspecified.",
    })
  }

  const unsupported = dateVars
    .filter((v) => trimmed(v.data_format) !== "" && !isSupportedDateFormat(v.data_format))
    .map(variableLabel)
  if (unsupported.length > 0) {
    errors.push({
      field: "transfer_metadata_schema.variables",
      tab: "transfer-metadata",
      message:
        `Unsupported date format on: ${unsupported.join(", ")}. ` +
        `Choose one of: ${SUPPORTED_DATE_FORMAT_TOKENS.join(", ")}.`,
    })
  }

  return errors
}
